import React, { useEffect, useRef, useState } from "react";
import { Icon, Intent, Position } from "@blueprintjs/core";
import { DateToLocal } from "components/dates";
import { DataDisplayTable } from "components/data-display";
import { Filter, FilterButton } from "components/filtering";
import { FormTextInput } from "components/form-fields";
import { ShowHide } from "components/layout";
import { ListingTable } from "components/listing";
import { NotificationInline, NotificationToaster, Modal } from "components/notifications";
import { useDebounce } from "hooks/useDebounce";
import PropTypes from "prop-types";
import { EmployeeAssessmentsService } from "services/EmployeeAssessmentsService";
import { Button } from "components/buttons";
import { Tooltip } from "components/tooltip";

const DEBOUNCE_DELAY_MS = 750;

export function EmployeeAssessments({ employeeId }) {
    const tableHeaders = ["Title", "Type", "Date Assigned", "Due Date", "Completed Date", "Result"];

    const sortableHeaders = [
        { headerName: "Title", sortName: "Title" },
        { headerName: "Type", sortName: "IsAssessment" },
        { headerName: "Date Assigned", sortName: "DateAssigned" },
        { headerName: "Due Date", sortName: "DueDate" },
        { headerName: "Completed Date", sortName: "CompletedDate" }
    ];

    const pageFilters = [
        {
            name: "IsAssessment",
            displayName: "Type",
            items: [
                { id: "true", name: "Assessment" },
                { id: "false", name: "Training" }
            ]
        },
        {
            name: "Completed",
            displayName: "Status",
            items: [
                { id: "true", name: "Completed" },
                { id: "false", name: "Not Completed" }
            ]
        }
    ];

    const [loadingData, setLoadingData] = useState(true);
    const [employeeExistsInExternalSystem, setEmployeeExistsInExternalSystem] = useState(false);
    const [reasonEmployeeWillNotBeSyncedType, setReasonEmployeeWillNotBeSyncedType] = useState(null);
    const [riskScores, setRiskScores] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState({ headerName: "Date Assigned", sortName: "DateAssigned" });
    const [sortedDir, setSortedDir] = useState("D");
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY_MS);
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [selectedAssessmentResult, setSelectedAssessmentResult] = useState(null);

    const currentPageNumber = useRef();
    const currentSortedBy = useRef();
    const currentSortedDir = useRef();
    const currentSearchTerm = useRef();
    const currentAppliedFilters = useRef();
    const inputSearchRef = useRef(null);

    function getRiskColour(risk) {
        switch (risk?.toLowerCase()) {
            case "high": return "EC2D6D";
            case "medium": return "FF7454";
            case "low": return "00C6B2";
            default: return null;
        }
    }

    function getNotSyncedMessage() {
        if (reasonEmployeeWillNotBeSyncedType === "InvalidCategory") {
            return "This employee cannot be synced as their category is not supported by the assessment system.";
        }

        if (reasonEmployeeWillNotBeSyncedType === "InvalidStatus") {
            return "This employee cannot be synced as their status is not supported by the assessment system.";
        }

        if (reasonEmployeeWillNotBeSyncedType === "NotDriverAppUser") {
            return "This employee is not a Vision app user.";
        }

        return "This employee has not been created in the third party system. Please check back later.";
    }

    function mapAssessmentRow(item) {
        const type = item.isAssessment ? "Assessment" : "Training";

        const dateAssigned = <DateToLocal key={`date-assigned-${item.id}`} format="DD/MM/YYYY">{item.dateAssigned}</DateToLocal>;
        const isOverdue = item.isOverdue;
        const dueDate = (
            <Tooltip
                content={isOverdue ? `${type} is overdue` : ""}
                position={Position.RIGHT}
                key={`due-date-${item.id}`}
            >
                <div className="inline-items nowrap">
                    <span>
                        <DateToLocal format="DD/MM/YYYY">{item.dueDate}</DateToLocal>
                    </span>
                    <ShowHide
                        evaluator={isOverdue}
                        show={<Icon icon="warning-sign" color="#F0578A" />}
                    />
                </div>
            </Tooltip>
        );
        const completedDate = <DateToLocal key={`completed-date-${item.id}`} format="DD/MM/YYYY">{item.completedDate}</DateToLocal>;

        let result = "-";

        if (item.assessmentResult) {
            const rating = item.assessmentResult.overallRiskRating;
            result = (
                <div className="inline-items" key={`result-${item.id}`}>
                    <span style={{ color: `#${getRiskColour(rating)}` }}>{rating}</span>
                    <Tooltip content="View risk scores">
                        <Button
                            onClick={() => setSelectedAssessmentResult(item.assessmentResult)}
                            icon="document-open"
                            iconOnly={true}
                            text="View risk scores"
                            large={false}
                            minimal={true}
                        />
                    </Tooltip>
                </div>
            );
        } else if (item.trainingResult) {
            result = `${item.trainingResult.score} / ${item.trainingResult.limit}`;
        }

        return [item.title, type, dateAssigned, dueDate, completedDate, result];
    }

    function fetchData() {
        if (
            currentPageNumber.current === pageNumber &&
            currentSortedBy.current === sortedBy &&
            currentSortedDir.current === sortedDir &&
            currentSearchTerm.current === debouncedSearchTerm &&
            currentAppliedFilters.current === appliedFilters
        ) {
            return;
        }

        currentPageNumber.current = pageNumber;
        currentSortedBy.current = sortedBy;
        currentSortedDir.current = sortedDir;
        currentSearchTerm.current = debouncedSearchTerm;
        currentAppliedFilters.current = appliedFilters;

        setLoadingData(true);

        const request = {
            requestCount: 25,
            pageNumber: pageNumber,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir,
            searchTerm: debouncedSearchTerm,
            filters: appliedFilters.map(function (f) {
                return { key: f.name, value: f.value };
            })
        };

        EmployeeAssessmentsService.searchForEmployee(employeeId, request).then(
            function (response) {
                setEmployeeExistsInExternalSystem(response.employeeExistsInExternalSystem);
                setReasonEmployeeWillNotBeSyncedType(response.reasonEmployeeWillNotBeSyncedType);

                setRiskScores({
                    overallRisk: response.overallRisk,
                    attitudeRisk: response.attitudeRisk,
                    knowledgeRisk: response.knowledgeRisk,
                    observationRisk: response.observationRisk,
                    hazardPerceptionRisk: response.hazardPerceptionRisk
                });

                setSummaryData({
                    lastAssessmentDate: response.lastAssessmentDate,
                    nextAssessmentDate: response.nextAssessmentDate,
                    lastTrainingDate: response.lastTrainingDate,
                    nextTrainingDate: response.nextTrainingDate,
                    hasCompletedInitialAssessment: response.hasCompletedInitialAssessment
                });

                setTableRows((response.data ?? []).map(mapAssessmentRow));
                setTotalRecords(response.totalCount);
                setLoadingData(false);

                setTimeout(function () {
                    if (inputSearchRef.current != null) {
                        inputSearchRef.current.focus();
                    }
                });
            },
            function () {
                NotificationToaster.show(Intent.DANGER, "Could not load assessments. Please try again.");
                setLoadingData(false);
            }
        );
    }

    useEffect(function () {
        fetchData();
    }, [pageNumber, sortedBy, sortedDir, debouncedSearchTerm, appliedFilters]);

    function renderRiskScore(risk) {
        if (!risk) {
            return <span>-</span>;
        }
        return <span style={{ color: `#${getRiskColour(risk)}` }}>{risk}</span>;
    }

    let hasCompletedInitialText = "-";
    if (summaryData) {
        hasCompletedInitialText = summaryData.hasCompletedInitialAssessment ? "Yes" : "No";
    }

    const overallRiskRowItems = [
        { header: "Overall Risk", value: renderRiskScore(riskScores?.overallRisk), key: "overall" },
        { header: "Has Completed Initial Assessment", value: hasCompletedInitialText, key: "initial" }
    ];

    const dimensionRiskRowItems = [
        { header: "Hazard Perception Risk", value: renderRiskScore(riskScores?.hazardPerceptionRisk), key: "hazard" },
        { header: "Attitude Risk", value: renderRiskScore(riskScores?.attitudeRisk), key: "attitude" },
        { header: "Knowledge Risk", value: renderRiskScore(riskScores?.knowledgeRisk), key: "knowledge" },
        { header: "Observation Risk", value: renderRiskScore(riskScores?.observationRisk), key: "observation" }
    ];

    // Row 3 — the four assessment and training dates.
    const dateRowItems = summaryData
        ? [
            {
                header: "Last Assessment Date",
                value: <DateToLocal key="last-assessment" format="DD/MM/YYYY">{summaryData.lastAssessmentDate}</DateToLocal>,
                key: "last-assessment"
            },
            {
                header: "Next Assessment Date",
                value: <DateToLocal key="next-assessment" format="DD/MM/YYYY">{summaryData.nextAssessmentDate}</DateToLocal>,
                key: "next-assessment"
            },
            {
                header: "Last Training Date",
                value: <DateToLocal key="last-training" format="DD/MM/YYYY">{summaryData.lastTrainingDate}</DateToLocal>,
                key: "last-training"
            },
            {
                header: "Next Training Date",
                value: <DateToLocal key="next-training" format="DD/MM/YYYY">{summaryData.nextTrainingDate}</DateToLocal>,
                key: "next-training"
            }
        ]
        : [];

    const riskScoreModalItems = selectedAssessmentResult
        ? [
            { header: "Overall Risk", value: renderRiskScore(selectedAssessmentResult.overallRiskRating), key: "overall" },
            { header: "Attitude Risk", value: renderRiskScore(selectedAssessmentResult.attitudeRiskRating), key: "attitude" },
            { header: "Knowledge Risk", value: renderRiskScore(selectedAssessmentResult.knowledgeRiskRating), key: "knowledge" },
            { header: "Observation Risk", value: renderRiskScore(selectedAssessmentResult.observationRiskRating), key: "observation" },
            { header: "Hazard Perception Risk", value: renderRiskScore(selectedAssessmentResult.hazardPerceptionRiskRating), key: "hazard" }
        ]
        : [];

    return (
        <div className="row">
            <h1>Assessments</h1>

            <ShowHide
                evaluator={!loadingData && !employeeExistsInExternalSystem}
                show={
                    <NotificationInline
                        intent="warning"
                        show={true}
                        text={getNotSyncedMessage()}
                    />
                }
            />

            <ShowHide
                evaluator={employeeExistsInExternalSystem || loadingData}
                show={
                    <>
                        <DataDisplayTable
                            data={overallRiskRowItems}
                            displayInRows={false}
                            loading={loadingData}
                            columnsPerRow={4}
                        />

                        <DataDisplayTable
                            data={dimensionRiskRowItems}
                            displayInRows={false}
                            loading={loadingData}
                            columnsPerRow={4}
                        />

                        <DataDisplayTable
                            data={dateRowItems}
                            displayInRows={false}
                            loading={loadingData}
                            columnsPerRow={4}
                        />

                    <div className="spacer-bottom-small">
                        <div className="filter-controls">
                            <div className="inline-items">
                                <div className="inline-item fill">
                                    <div className="form-field-full">
                                        <FormTextInput
                                            inputRef={inputSearchRef}
                                            placeholder="Search assessments"
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            value={searchTerm}
                                            large
                                            loading={loadingData}
                                            icon="search"
                                            id="assessments-search-field"
                                        />
                                    </div>
                                </div>
                                <div className="inline-item">
                                    <FilterButton
                                        onToggle={() => setShowFilters((p) => !p)}
                                        filters={appliedFilters}
                                    />
                                </div>
                            </div>
                            <Filter
                                filterName="employeeAssessments"
                                filters={pageFilters}
                                visible={showFilters}
                                onUpdate={(f) => setAppliedFilters(f)}
                                onClickOutside={() => setShowFilters((p) => !p)}
                            />
                        </div>
                    </div>

                        <ListingTable
                            id="listing-table-assessments"
                            headers={tableHeaders}
                            data={tableRows}
                            totalRecordCount={totalRecords}
                            loadingData={loadingData}
                            noDataMessage="No assessments or training records found."
                            sortable
                            sortableHeaders={sortableHeaders}
                            sortedBy={sortedBy}
                            sortedDir={sortedDir}
                            onSort={(header, direction) => { setSortedDir(direction); setSortedBy(header); }}
                            pageSize={25}
                            onPagingChange={(page) => setPageNumber(page)}
                        />
                    </>
                }
            />

            <Modal
                isOpen={selectedAssessmentResult !== null}
                title="Assessment Risk Scores"
                onClose={() => setSelectedAssessmentResult(null)}
            >
                <DataDisplayTable
                    data={riskScoreModalItems}
                    displayInRows={false}
                    columnsPerRow={5}
                />
            </Modal>
        </div>
    );
}

EmployeeAssessments.propTypes = {
    employeeId: PropTypes.string.isRequired
};
